import { useEffect, useState } from 'react';

/**
 * Notice when a new version of the interface has been deployed underneath us.
 *
 * The problem this solves is specific and had already cost the client time three times.
 * This is a single page: once a browser has it open, moving between the board, the actions
 * and the plans fetches no new code. A tab left open across a deploy keeps running the old
 * program indefinitely, and nothing on screen says so — so a feature that shipped an hour
 * ago is simply "not there" as far as the person looking at it is concerned.
 *
 * It matters more here than in most software. A control room leaves this on a wall display
 * for days at a time, and several people in an exercise can end up on different versions
 * without anyone realising.
 *
 * The check is deliberately dumb: remember what the server said when we loaded, ask again
 * later, and if the answer has changed then a deploy happened. The browser never needs to
 * know its own version, which means nothing has to be threaded through the build.
 *
 * It never reloads on its own. Somebody may be halfway through typing a log entry when the
 * deploy lands, and throwing that away to be helpful is worse than being one version behind.
 */

/**
 * Every minute. Deliberately not longer.
 *
 * The first version of this hung the check off the WebSocket reconnecting, on the reasoning
 * that a deploy restarts the service and drops every socket. That was too clever: the socket
 * only exists while an incident is open, so anybody sitting on the Plans or People screen
 * had no trigger at all, and if the socket failed to come back the trigger never fired
 * either. A plain interval has none of those dependencies. The request is a few bytes.
 */
const EVERY = 60 * 1000;

export function NewVersion() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    let mine = null;      // what the server said when this tab loaded
    let stop = false;

    async function check() {
      try {
        const res = await fetch('/api/version', { cache: 'no-store' });
        if (!res.ok) return;
        const { build } = await res.json();
        if (stop || !build) return;
        if (mine === null) { mine = build; return; }
        if (build !== mine) setStale(true);
      } catch {
        // Offline, or the server is mid-restart during the very deploy we are watching for.
        // Either way there is nothing to say yet; the next check will find it.
      }
    }

    check();
    const timer = setInterval(check, EVERY);

    // A deploy restarts the service, so every browser's socket drops and comes back. That
    // reconnection is the earliest and most reliable moment to look, and it costs nothing.
    // Extra prompts on top of the interval, none of them load-bearing on their own: coming
    // back to the tab, and the socket reconnecting after the service restarted.
    const again = () => check();
    const onVisible = () => { if (!document.hidden) check(); };
    window.addEventListener('focus', again);
    window.addEventListener('imtlogic:reconnected', again);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stop = true;
      clearInterval(timer);
      window.removeEventListener('focus', again);
      window.removeEventListener('imtlogic:reconnected', again);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!stale) return null;

  return (
    <div className="newVersion" role="status">
      <span>
        <b>A newer version of IMT Logic has been installed.</b> You are still running the one
        loaded when you opened this tab.
      </span>
      <span className="spacer" />
      <button className="btn narrow" onClick={() => window.location.reload()}>
        Reload now
      </button>
      {/* Dismissable, because "reload now" is the wrong answer in the middle of an incident.
          It returns at the next check rather than being silenced for good. */}
      <button className="hoX" onClick={() => setStale(false)} aria-label="Not now">×</button>
    </div>
  );
}
